/**
 * Running the risk engine.
 *
 * `computePortfolioRisk` is what the admin console's Risk Analysis tab calls: it
 * runs the Monte Carlo forecast for one portfolio under one scenario, persists
 * the portfolio-level snapshot and the per-holding contributions, and returns
 * the freshly computed row. It is a mutation because of that write — there is no
 * read-only equivalent, which is why a screen that wants scenario risk has to
 * ask for it rather than subscribe to it.
 */
import { gql } from '@apollo/client';

/**
 * Forecast risk for one portfolio under one scenario.
 *
 * `scenarioName` takes the scenario's **`scenarioValue` slug**
 * (`covid_19_market_crash`), not its display name; the response echoes the
 * display name back in its own `scenarioName`, alongside the slug in
 * `scenarioValue`.
 *
 * `runDate` is the day being measured: it selects that day's holdings, and it
 * is the `latestDate` the result is persisted under. Optional — the resolver
 * defaults it to today.
 *
 * `simulationRunDate` names a different stored simulation partition to price
 * from, instead of `runDate`'s own. It is what lets one simulation — today's,
 * say — serve a backfill of several past run dates without rebuilding the
 * universe for each. Holdings absent from that partition drop out of the
 * metrics, so it is left unset unless a backfill is what is being asked for.
 *
 * `riskConfigId` is deliberately not sent. The resolver defaults it to
 * `parametric_full`, which is the config every stored row on this backend was
 * computed under — sending anything else would produce metrics that cannot be
 * compared with what the rest of the app already shows.
 *
 * Every metric field is nullable and sparsely populated: a scenario whose
 * partition has no benchmark simulation vector returns nulls for alpha, beta
 * and tracking error while the rest computes fine. Callers must render what is
 * present rather than assume a full row.
 *
 * `urlId` is the handle this run is stored under for the agent's report (see
 * RESULT_HEAD in queries/attribution.js).
 */
export const COMPUTE_PORTFOLIO_RISK = gql`
  mutation ComputePortfolioRisk(
    $securitySymbol: String!
    $scenarioName: String!
    $runDate: Date
    $simulationRunDate: Date
  ) {
    computePortfolioRisk(
      securitySymbol: $securitySymbol
      scenarioName: $scenarioName
      runDate: $runDate
      simulationRunDate: $simulationRunDate
    ) {
      urlId
      runDate
      latestDate
      riskConfigId
      riskType
      scenarioName
      scenarioValue
      expectedReturn
      volatility
      benchmarkVolatility
      downsideVolatility
      upsideVolatility
      sharpeRatio
      sortinoRatio
      calmarRatio
      informationRatio
      maxDrawdown
      currentDrawdown
      maxDrawdownDuration
      var95
      var99
      cvar95
      cvar99
      alpha
      betaToBenchmark
      correlationToBenchmark
      trackingError
      riskScore
      riskVsBenchmark
      riskVsTarget
      holdingRiskContributions {
        holdingSecuritySymbol
        name
        weight
        riskType
        forecastExpectedReturn
        forecastVolatility
        forecastVar95
        forecastVar99
        forecastCvar95
        forecastCvar99
        forecastAlpha
        forecastBetaToBenchmark
        forecastTrackingError
        forecastInformationRatio
      }
    }
  }
`;

/**
 * A portfolio's factor exposures, computed on demand.
 *
 * `coveragePercentage` is the share of the book the factor model could price;
 * a run that succeeds with low coverage is a real answer about a book the model
 * does not reach, not a failure, and the screen says which.
 */
export const COMPUTE_PORTFOLIO_EXPOSURES = gql`
  mutation PortfolioFactorExposures($portfolioSymbol: String!, $asOfDate: String, $startDate: String, $endDate: String) {
    portfolioFactorExposures(
      portfolioSymbol: $portfolioSymbol
      asOfDate: $asOfDate
      startDate: $startDate
      endDate: $endDate
    ) {
      success
      message
      asOfDate
      coveragePercentage
      totalHoldings
      holdingsWithFactorData
    }
  }
`;

/**
 * Run the fundamental style factor model for ONE portfolio, and wait for it.
 *
 * Synchronous: the answer arrives when the run has finished, so nothing polls.
 * `fromDate` / `toDate` are the days it covered.
 *
 * The run clears only the portfolio OWNER's cached `getPortfolio`. A desk
 * reading the portfolio on a client's behalf holds its own copy, which the run
 * does not touch — so the caller must re-read past the cache afterwards
 * (rereadPortfolioOnScreen), or the screen keeps showing the empty answer.
 */
export const RUN_FUNDAMENTAL_STYLE_FACTORS = gql`
  mutation RunFundamentalStyleFactors($securitySymbol: String!) {
    runFundamentalStyleFactors(securitySymbol: $securitySymbol) {
      success
      message
      fromDate
      toDate
    }
  }
`;
